// RecipeDashboard.jsx - Malzeme sekmesi eklenmiş hali
import React, { useState, useEffect } from 'react';
import { FaClipboardList, FaUtensils, FaChartBar, FaPizzaSlice } from 'react-icons/fa';
import './RecipeDashboard.css';
import MenuItemSelector from './MenuItemSelector';
import RecipeCostAnalysis from './RecipeCostAnalysis';
import RecipeManagement from './RecipeManagement';
import IngredientManagement from './IngredientManagement';
import api from '../services/api.service';

const RecipeDashboard = () => {
  const [activeTab, setActiveTab] = useState('recipes');
  const [selectedRecipeData, setSelectedRecipeData] = useState(null);
  const [recipeStats, setRecipeStats] = useState({
    totalRecipes: 0,
    completedRecipes: 0,
    pendingRecipes: 0,
    averageCost: 0,
    averageMargin: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // İstatistikleri yükle
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        // Gerçek bir uygulamada, bu bilgileri bir API uç noktasından alırdınız
        // Şimdilik, reçeteleri getirerek istatistikleri hesaplayacağız
        const { data: recipes } = await api.getRecipes();
        
        // İstatistikleri hesapla
        const totalRecipes = recipes.length;
        const menuItemsWithRecipes = new Set(recipes.map(r => r.menuItem.id));
        
        // Reçetesi olmayan menü öğelerini hesaplamak için tüm menü öğelerini al
        const { data: menuItems } = await api.getMenuItems({ isActive: true });
        const pendingRecipes = menuItems.length - menuItemsWithRecipes.size;
        
        // Ortalama maliyet ve marj hesaplama
        let totalCost = 0;
        let totalMargin = 0;
        
        recipes.forEach(recipe => {
          const cost = parseFloat(recipe.calculatedCost) || 0;
          const price = recipe.menuItem.price || 0;
          const margin = price - cost;
          
          totalCost += cost;
          totalMargin += margin;
        });
        
        const averageCost = totalRecipes > 0 ? totalCost / totalRecipes : 0;
        const averageMargin = totalRecipes > 0 ? totalMargin / totalRecipes : 0;
        
        setRecipeStats({
          totalRecipes,
          completedRecipes: totalRecipes,
          pendingRecipes,
          averageCost,
          averageMargin
        });
        
        setError(null);
      } catch (err) {
        console.error("İstatistik yükleme hatası:", err);
        setError("İstatistikler yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);
  
  // Kayıtlı reçete oluşturma isteğini kontrol et
  useEffect(() => {
    const storedMenuItemId = sessionStorage.getItem('createRecipeForMenuItem');
    if (storedMenuItemId) {
      // Yeniden tetiklenmesini önlemek için kayıtlı değeri temizle
      sessionStorage.removeItem('createRecipeForMenuItem');
      
      // Seçilen reçete verilerini ayarla
      setSelectedRecipeData({
        menuItemId: storedMenuItemId,
        isExisting: false
      });
      
      // Reçeteler sekmesinde olduğumuzdan emin ol
      setActiveTab('recipes');
    }
  }, []);
  
  // Menü öğesi seçimi için işleyici
  const handleMenuItemSelect = (id, isExisting) => {
    // Reçeteler sekmesine geç
    setActiveTab('recipes');
    
    // Seçilen reçete verilerini ayarla
    setSelectedRecipeData({
      // isExisting true ise id recipeId'dir. Aksi takdirde menuItemId'dir
      [isExisting ? 'recipeId' : 'menuItemId']: id,
      isExisting: isExisting
    });
  };
  
  return (
    <div className="recipe-dashboard">
      <h1 className="dashboard-title">Reçete Yönetimi</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      {/* İstatistik Özeti */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-value">{recipeStats.totalRecipes}</div>
          <div className="stat-label">Toplam Reçete</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-value">{recipeStats.pendingRecipes}</div>
          <div className="stat-label">Reçetesi Olmayan Ürün</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-value">₺{recipeStats.averageCost.toFixed(2)}</div>
          <div className="stat-label">Ortalama Maliyet</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-value">₺{recipeStats.averageMargin.toFixed(2)}</div>
          <div className="stat-label">Ortalama Kar Marjı</div>
        </div>
      </div>
      
      <div className="tab-navigation">
        <div 
          className={`tab-item ${activeTab === 'recipes' ? 'active' : ''}`}
          onClick={() => setActiveTab('recipes')}
        >
          <FaClipboardList className="tab-icon" />
          <span className="tab-label">Reçeteler</span>
        </div>
        
        <div 
          className={`tab-item ${activeTab === 'menu-items' ? 'active' : ''}`}
          onClick={() => setActiveTab('menu-items')}
        >
          <FaUtensils className="tab-icon" />
          <span className="tab-label">Menü Öğeleri</span>
        </div>
        
        <div 
          className={`tab-item ${activeTab === 'ingredients' ? 'active' : ''}`}
          onClick={() => setActiveTab('ingredients')}
        >
          <FaPizzaSlice className="tab-icon" />
          <span className="tab-label">Malzemeler</span>
        </div>
        
        <div 
          className={`tab-item ${activeTab === 'analysis' ? 'active' : ''}`}
          onClick={() => setActiveTab('analysis')}
        >
          <FaChartBar className="tab-icon" />
          <span className="tab-label">Maliyet Analizi</span>
        </div>
      </div>
      
      <div className="tab-content">
        {activeTab === 'recipes' && (
          <RecipeManagement 
            initialRecipeData={selectedRecipeData} 
            onRecipeAction={() => setSelectedRecipeData(null)} 
          />
        )}
        {activeTab === 'menu-items' && <MenuItemSelector onMenuItemSelect={handleMenuItemSelect} />}
        {activeTab === 'ingredients' && <IngredientManagement />}
        {activeTab === 'analysis' && <RecipeCostAnalysis />}
      </div>
    </div>
  );
};

export default RecipeDashboard;